import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { colors, fonts, glyphs, useMediaQuery } from '../../Styles';
import { Overlay, LineInfoDisplay, FieldMode } from '..';
import { ButtonTypes, SelectFieldOption_Values } from '../../Definitions';
import { useAppSelector } from '../../Store';
import { Graph_QL } from '../../Services';
import { useAnimal } from '../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal';
import { MaintainAnimal } from '../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall';
import { LocaleFarmDate } from '../../Definitions/localeFarmDate';
import { UndefinedOptions, useGroupOptions, useSectionOptions } from '../_Components';
import NewButton, { NewButtonList } from '../BaseComponents/newButton';
// import { useDateFormat } from '../_Components';

interface basicInfo_Props {
  selectedAnimal: number
}
/**
 * BasicInfo
 * @param selectedAnimal ID for the animal to display information about.
 * @returns JSX element displaying basic info about the selected animal.
 */
export default function BasicInfo({ selectedAnimal }:basicInfo_Props) {
  const {t} = useTranslation()
  const [editBasicData, setEditBasicData] = useState<boolean>(false)
  const groupOptions = useGroupOptions(UndefinedOptions.undefined)
  const {locationOptions} = useSectionOptions(UndefinedOptions.undefined)

  const mobileXs = useMediaQuery(`(max-width: 450px)`);
  const dirtRef = useRef<MaintainAnimal>({animal_id: selectedAnimal});
  // const {formatDateForDisplay, formatDateForInput} = useDateFormat();

  useEffect(() => {
    dirtRef.current.animal_id = selectedAnimal;
  },[selectedAnimal])

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);
  
  const { data: animalsState, maintainEntries: updateAnimals } = useAnimal(connection);

  const thisAnimal = useMemo(() => {
    return animalsState && animalsState[domain] ? animalsState[domain].data.find((animal) => animal.animal_id === selectedAnimal) : undefined
  },[animalsState, domain, selectedAnimal])
    
  const AnimalInfo = useMemo(() => {
    const animalNo = thisAnimal && thisAnimal.animal_number
    // const type = thisAnimal && thisAnimal.animalType ? typeOptions?.find(({id}) => (thisAnimal.animalType === id)) : undefined
    const lifeNo = (thisAnimal && thisAnimal.life_number) ?? undefined
    const responderNo = (thisAnimal && thisAnimal.responder_number) ?? undefined
    // const condition = thisAnimal&&thisAnimal.animalCondition?.conditionId ? conditionOptions.find(({id}) => thisAnimal.animalCondition?.conditionId === id) : undefined
    const group = thisAnimal&&thisAnimal.animalGroup.groupId ? groupOptions.find(({id}) => thisAnimal.animalGroup.groupId === id) : undefined
    const location = thisAnimal && thisAnimal.location_id ? locationOptions?.find(({id}) => (thisAnimal.location_id === id)) : undefined
    const birthDate = thisAnimal && thisAnimal.birthday

    return {
      animalNo,
      // type,
      lifeNo,
      responderNo,
      // condition,
      group,
      location,
      birthDate
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisAnimal])

  // #region | LineInfoDisplays
  const basic_animalId = <LineInfoDisplay 
    editMode={editBasicData}
    value={AnimalInfo.animalNo}
    header={t("params.animalId", {defaultValue: "Animal No."})}
    icon={"AnimalNumber"}
    fieldMode={FieldMode.input}
    onchange={(e: string|number) => dirtRef.current.animal_number = Number(e)}
    defaultValue={AnimalInfo.animalNo}
    isDisabled
  />

  const basic_animalType = <LineInfoDisplay 
    editMode={editBasicData}
    isDisabled
    value={/*type?.label*/ undefined}
    undefinedValue={t("negativeParams.animalType", {defaultValue: "No Animal Type"})}
    header={t("params.animalType", {defaultValue: "Animal Type"})}
    icon={"AnimalType"}
    fieldMode={FieldMode.input}
    // onchange={(option) => dirtRef.current.animalType = Number(option?.id)} TODO
    // options={/*TODO: typeOptions*/ []}
    // defaultValue={typeOptions?.find(({id}) => type?.id === id)} TODO
    isClearable
  />

  const basic_animalLifeNo = <LineInfoDisplay 
    editMode={editBasicData}
    value={AnimalInfo.lifeNo}
    header={t("params.lifeNo", { defaultValue: "Life No." })}
    undefinedValue={t("negativeParams.lifeNo", { defaultValue: "No Life Mo." })}
    icon={"LifeNumber"}
    fieldMode={FieldMode.input}
    onchange={(e: string|number) => dirtRef.current.life_number = e ? Number(e) : NaN}
    defaultValue={AnimalInfo.lifeNo ?? undefined}
    isClearable
    type='number'
  />

  const basic_animalResponderNo = <LineInfoDisplay 
    editMode={editBasicData}
    value={AnimalInfo.responderNo}
    header={t("params.responderNo", { defaultValue: "Responder No." })}
    undefinedValue={t("negativeParams.responderNo", { defaultValue: "No Responder No." })}
    icon={"ResponderNumber"} 
    fieldMode={FieldMode.input}
    onchange={(e: string|number) => dirtRef.current.responder_number = e ? Number(e) : NaN}
    defaultValue={AnimalInfo.responderNo ?? undefined}
    isClearable
    type='number'
  />

  const basic_animalCondition = <LineInfoDisplay 
    editMode={editBasicData}
    isDisabled
    value={/*condition?.label*/ undefined} // REMEBMER LABEL STYLE
    header={t("params.condition", { defaultValue: "Condition" })}
    undefinedValue={t("negativeParams.condition", { defaultValue: "No Condition" })}
    icon={"Condition"}
    fieldMode={FieldMode.select}
    // onchange={(option) => dirtRef.current.condition = Number(option?.id)} TODO
    options={/*TODO: conditionOptions*/ []}
    // defaultValue={conditionOptions?.find(({id}) => condition?.id === id)} TODO
    isClearable
  />

  const basic_animalGroup = <LineInfoDisplay 
    editMode={editBasicData}
    value={AnimalInfo.group?.label}
    header={t("params.group", { defaultValue: "Group" })}
    undefinedValue={t("negativeParams.group", { defaultValue: "No Group" })}
    icon={"AnimalGroup"}
    fieldMode={FieldMode.select}
    onchange={(option: SelectFieldOption_Values|undefined) => dirtRef.current.animal_group_id = option?.id ? Number(option?.id) : NaN}
    options={groupOptions}
    defaultValue={groupOptions?.find(({ id }) => AnimalInfo.group?.id === id) ?? undefined}
    isClearable
  />

  const basic_animalLocation = <LineInfoDisplay 
    editMode={editBasicData}
    value={AnimalInfo.location?.label}
    header={t("params.location", { defaultValue: "Location" })}
    undefinedValue={t("negativeParams.location", { defaultValue: "No Location" })}
    icon={"Location"}
    fieldMode={FieldMode.select}
    onchange={(option: SelectFieldOption_Values|undefined) => dirtRef.current.location = option?.id ? Number(option?.id) : NaN}
    options={locationOptions}
    defaultValue={locationOptions?.find(({ id }) => AnimalInfo.location?.id === id) ?? undefined}
    isClearable
  />

  const basic_animalBirthDate = <LineInfoDisplay 
    editMode={editBasicData}
    value={AnimalInfo.birthDate ? new LocaleFarmDate(AnimalInfo.birthDate).toDateForDisplay() : undefined}
    header={t("params.birthDate", { defaultValue: "Birth Date" })}
    undefinedValue={t("negativeParams.birthDate", { defaultValue: "No Birth Date" })}
    icon={"BirthDate"}
    fieldMode={FieldMode.input}
    type='date'
    onchange={(e: string|number) => dirtRef.current.birthday = (new Date(e).getTime()/1000)}
    defaultValue={AnimalInfo.birthDate ? new LocaleFarmDate(AnimalInfo.birthDate).toDateForInput(): ""}
    isClearable
  />
  // #endregion

  return ( <>
    { editBasicData ? <Overlay onClickAlert beforeUnload/> : <></> }
    <div style={{zIndex: editBasicData? "999" : "0", width: "100%", boxSizing: "border-box", position: "relative", height: "100%"}}>
      <div style={{width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "end", boxSizing: "border-box", padding: "0px 20px"}}>
        <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary, marginBottom: "5px"}}>{t("animalPages.animal.basicInfo", {defaultValue: "Basic Info"})}</p>

        { editBasicData
          ? <NewButtonList 
              contents={[t("buttons.save", {defaultValue: "save"}), t("buttons.cancel", {defaultValue: "cancel"}), t("buttons.edit", {defaultValue: "edit"})]} 
              onClick={(index: number) => {
                if (index === 0) {setEditBasicData(false); if (updateAnimals) {updateAnimals({data: [dirtRef.current]});}; dirtRef.current = {animal_id: selectedAnimal}} 
                if (index === 1) {setEditBasicData(false); dirtRef.current = {animal_id: selectedAnimal}}
                if (index === 2) {}
              }}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[glyphs.Check({}), glyphs.Close({}), glyphs.Edit({})]}
              disabled={[false, false, true]}
              overrides={(index) => ({
                baseStyles: {
                  backgroundColor: index === 2 ? colors.greyPrimary : colors.greySecondary
                }
              })}
            />
          : <NewButton content={t("buttons.edit", {defaultValue: "edit"})} startEnhancer={glyphs.Edit({})} onClick={() => setEditBasicData(true)} buttonType={ButtonTypes.tableButton}/>
        }
      </div>

      <div style={{overflowY: "scroll", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: colors.greySecondary+colors.transparent, backgroundColor: colors.greyPrimary, padding: "20px", width: "100%", height: "calc(100% - 50px)", borderRadius: "10px", boxSizing: "border-box"}}>
        { !thisAnimal
          ? <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%", ...fonts.fontSize3, ...fonts.paragraph, color: colors.greyLightTertiary}}>{t("animalPages.animal.noAnimalSelected", {defaultValue: "No animal selected"})}</div>
          : <div style={{display: "flex", flexWrap: "wrap", margin: "0 auto", gap: "10px", 
              // width: colThree ? "990px" : colTwo ? "650px" : "320px"
              width: mobileXs ? "270px" : "320px"
            }}>
              {basic_animalId}{basic_animalType}
              {basic_animalLifeNo}{basic_animalResponderNo}
              {basic_animalCondition}{basic_animalGroup}
              {basic_animalLocation}{basic_animalBirthDate}
            </div>
        }
      </div>
    </div>
  </> );
}